import { ChangeEvent, KeyboardEvent, useMemo, useState } from "react";
import { PlusIcon, X } from "lucide-react";
import Box from "@/common/components/Box";
import Select from "@/common/components/Select";
import TextField from "@/common/components/TextField";
import Button from "@/common/components/Button";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { Option } from "@/common/types/option";
import { MissionCondition } from "../types/mission.type";
import { parseRequirementQuantity } from "../utils/missionMapper";
import { MissionRequirementsTransformer } from "../services/missionRequirementsTransformer";

type RequirementsSectionProps = {
  conditions: MissionCondition[];
  onAddCondition: (condition: Omit<MissionCondition, "id">) => void;
  onRemoveCondition: (id: string) => void;
  onUpdateCondition: (id: string, data: Partial<MissionCondition>) => void;
  isSubmitting: boolean;
  conditionTypeOptions: Option[];
  errorMessage?: string;
};

type DraftCondition = Omit<MissionCondition, "id">;

const EMPTY_DRAFT: DraftCondition = {
  condition_group_label: "general",
  type_label: "",
  value_label: "",
  provider_klass: "",
};

export const RequirementsSection = ({
  conditions,
  onAddCondition,
  onRemoveCondition,
  onUpdateCondition,
  isSubmitting,
  conditionTypeOptions,
  errorMessage,
}: RequirementsSectionProps) => {
  const [draft, setDraft] = useState<DraftCondition>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [isAddingRequirement, setIsAddingRequirement] = useState(false);

  const filteredConditions = useMemo(() => conditions, [conditions]);
  const normalizeSingleOption = (value: unknown): Option | null => {
    if (!value || Array.isArray(value)) return null;
    return value as Option;
  };

  const handleDraftChange = (field: keyof DraftCondition, value: string) => {
    const nextValue =
      field === "value_label"
        ? MissionRequirementsTransformer.sanitizePositiveNumericValue(value)
        : value;
    setDraft((prev) => ({ ...prev, [field]: nextValue }));
    setDraftError(null);
  };

  const handleTypeChange = (option: Option | null) => {
    setDraft((prev) => ({
      ...prev,
      type_label: option?.label || "",
      provider_klass: option?.value ? String(option.value) : "",
      value_label: option
        ? MissionRequirementsTransformer.fallbackValue(option.label)
        : "",
    }));
    setDraftError(null);
  };

  const handleNumericKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const allowedNavigationKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
      "Enter",
    ];
    const decimalKeys = [",", "."];
    if (event.ctrlKey || event.metaKey) return;
    if (allowedNavigationKeys.includes(event.key)) return;
    if (decimalKeys.includes(event.key)) return;
    if (/^[0-9]$/.test(event.key)) return;
    event.preventDefault();
  };

  const handleAddConditionClick = () => {
    if (!draft.type_label || !draft.provider_klass) {
      setDraftError("Selecione o tipo do requisito");
      return;
    }

    const valueRequired =
      MissionRequirementsTransformer.isValueRequired(draft.type_label);

    if (valueRequired) {
      if (!draft.value_label) {
        setDraftError("Esse campo aceita apenas números.");
        return;
      }

      const numericValue = parseRequirementQuantity(draft.value_label);
      if (!numericValue || numericValue <= 0) {
        setDraftError("Informe um valor maior que zero.");
        return;
      }
    }

    onAddCondition({
      ...draft,
      condition_group_label: draft.condition_group_label || "general",
    });
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
    setIsAddingRequirement(false);
  };

  const resolveTypeOption = (provider?: string, label?: string) => {
    if (!provider && !label) return null;
    return (
      conditionTypeOptions.find(
        (option) => option.value === provider || option.label === label,
      ) ||
      (provider || label
        ? {
            label: label || provider || "",
            value: provider || label || "",
          }
        : null)
    );
  };

  return (
    <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="flex flex-col gap-1">
        <h2 className="text-[22px] font-bold text-white">
          Requisitos da missão
        </h2>
        <span className="text-gray-400 text-sm">
          (Todos devem ser cumpridos para finalizar)
        </span>
      </div>

      <div className="flex flex-col gap-4">
        {filteredConditions.length === 0 && (
          <span className="text-gray-400 text-sm">
            Adicione um requisito usando o formulário abaixo.
          </span>
        )}

        {filteredConditions.map((condition) => (
          <div
            key={condition.id}
            className="flex bg-neutral-op-8 rounded-md overflow-hidden min-h-[84px]"
          >
            <div className="w-[4px] bg-[#2b88d8] shrink-0" />

            <div className="flex items-center justify-center px-4 shrink-0">
              <button
                type="button"
                onClick={() => onRemoveCondition(condition.id)}
                className="text-danger hover:text-red-400 transition-colors"
                disabled={isSubmitting}
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 p-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <Select
                label="Tipo"
                options={conditionTypeOptions}
                value={resolveTypeOption(
                  condition.provider_klass,
                  condition.type_label,
                )}
                onChange={(option) => {
                  const normalized = normalizeSingleOption(option);
                  const nextLabel = normalized ? String(normalized.label) : "";
                  onUpdateCondition(condition.id, {
                    provider_klass: normalized ? String(normalized.value) : "",
                    type_label: nextLabel,
                    value_label:
                      MissionRequirementsTransformer.isValueRequired(nextLabel)
                        ? ""
                        : "1",
                  });
                }}
                isDisabled={isSubmitting}
              />

              {MissionRequirementsTransformer.isValueRequired(
                condition.type_label,
              ) && (
                <div className="flex flex-col gap-1">
                  <TextField
                    label="Valor"
                    type="text"
                    inputMode="decimal"
                    value={condition.value_label}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onUpdateCondition(condition.id, {
                        value_label:
                          MissionRequirementsTransformer.sanitizePositiveNumericValue(
                            event.target.value,
                          ),
                      })
                    }
                    onKeyDown={handleNumericKeyDown}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {isAddingRequirement ? (
        <div className="bg-neutral-op-8 rounded-md p-4 flex flex-col gap-4">
          <Select
            label="Tipo do requisito"
            placeholder="Selecione"
            options={conditionTypeOptions}
            value={resolveTypeOption(draft.provider_klass, draft.type_label)}
            onChange={(option) =>
              handleTypeChange(normalizeSingleOption(option))
            }
            isDisabled={isSubmitting || conditionTypeOptions.length === 0}
          />

          {draft.provider_klass &&
            MissionRequirementsTransformer.isValueRequired(
              draft.type_label,
            ) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <TextField
                  label="Valor"
                  placeholder="Ex: 50"
                  type="text"
                  inputMode="decimal"
                  value={draft.value_label}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleDraftChange("value_label", event.target.value)
                  }
                  onKeyDown={handleNumericKeyDown}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          )}

          {draftError && (
            <ErrorMessage name="draft_condition" message={draftError} />
          )}

          <div className="flex flex-col gap-2 md:flex-row md:justify-end md:gap-4">
            <Button
              type="button"
              variant="outline-text"
              onClick={() => {
                setIsAddingRequirement(false);
                setDraft(EMPTY_DRAFT);
                setDraftError(null);
              }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline-text"
              onClick={handleAddConditionClick}
              disabled={isSubmitting || conditionTypeOptions.length === 0}
            >
              <span className="flex items-center gap-2">
                <PlusIcon size={18} /> Salvar requisito
              </span>
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsAddingRequirement(true)}
          className="bg-neutral-op-8 rounded-md px-4 py-3 text-left text-gray-300 border border-dashed border-gray-600 hover:border-white transition-colors"
          disabled={isSubmitting || conditionTypeOptions.length === 0}
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <PlusIcon size={18} /> Adicionar requisito
          </span>
        </button>
      )}

      {errorMessage && (
        <ErrorMessage name="conditions" message={errorMessage} />
      )}
    </Box>
  );
};
